import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Link } from "@/i18n/LangRouting";
import { useI18n } from "@/i18n/I18nContext";
import { usePlayer } from "@/visitor/PlayerContext";

const KEY = "bg_cookie_consent"; // "accepted" | "rejected"
const GA_ID = process.env.REACT_APP_GA_MEASUREMENT_ID;
export const OPEN_EVENT = "bg-open-cookie-settings";

function gtag() { window.dataLayer = window.dataLayer || []; window.dataLayer.push(arguments); }

function ensureGtag() {
  if (!GA_ID || window.__bgGtagLoaded) return;
  window.__bgGtagLoaded = true;
  gtag("js", new Date());
  gtag("config", GA_ID, { send_page_view: false, anonymize_ip: true });
  const s = document.createElement("script"); s.async = true; s.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`; document.head.appendChild(s);
}

function setDefaultConsent() {
  if (window.__bgConsentDefault) return;
  window.__bgConsentDefault = true;
  gtag("consent", "default", { analytics_storage: "denied", ad_storage: "denied", ad_user_data: "denied", ad_personalization: "denied", wait_for_update: 500 });
}

export function applyConsent(choice) {
  setDefaultConsent();
  if (choice === "accepted") {
    gtag("consent", "update", { analytics_storage: "granted", ad_storage: "denied", ad_user_data: "denied", ad_personalization: "denied" });
    ensureGtag();
  } else {
    gtag("consent", "update", { analytics_storage: "denied", ad_storage: "denied", ad_user_data: "denied", ad_personalization: "denied" });
  }
}

export default function CookieConsent() {
  const { t } = useI18n();
  const { track } = usePlayer();
  const location = useLocation();
  const [choice, setChoice] = useState(() => localStorage.getItem(KEY));
  const [open, setOpen] = useState(() => !localStorage.getItem(KEY));

  useEffect(() => { setDefaultConsent(); if (choice) applyConsent(choice); }, []); // eslint-disable-line

  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener(OPEN_EVENT, handler);
    return () => window.removeEventListener(OPEN_EVENT, handler);
  }, []);

  useEffect(() => {
    if (choice !== "accepted" || !GA_ID) return;
    gtag("event", "page_view", { page_path: location.pathname + location.search, page_location: window.location.href, page_title: document.title });
  }, [choice, location.pathname, location.search]);

  const decide = (c) => { localStorage.setItem(KEY, c); setChoice(c); applyConsent(c); setOpen(false); };
  if (!open || !GA_ID) return null;

  return (
    <div className={`fixed inset-x-0 z-[45] px-4 ${track ? "bottom-[128px]" : "bottom-0"}`} data-testid="cookie-banner" role="dialog" aria-label={t("cookie_settings")}>
      <div className="max-w-6xl mx-auto bg-[#1d1d1d] border border-[var(--line)] shadow-2xl p-3 sm:px-4 flex flex-col sm:flex-row sm:items-center gap-3 text-xs">
        <p className="flex-1 text-[#d8d8d8] leading-snug">{t("cookie_text")} <Link to="/legal/privacy" className="underline text-white hover:text-accent" data-testid="cookie-privacy-link">{t("privacy_policy")}</Link></p>
        <div className="grid grid-cols-2 gap-2 sm:w-64">
          <button type="button" onClick={() => decide("rejected")} className="border border-[#777] text-white font-bold uppercase tracking-widest py-2.5 hover:border-white transition-colors" data-testid="cookie-reject">{t("cookie_reject")}</button>
          <button type="button" onClick={() => decide("accepted")} className="border border-[#777] text-white font-bold uppercase tracking-widest py-2.5 hover:border-white transition-colors" data-testid="cookie-accept">{t("cookie_accept")}</button>
        </div>
      </div>
    </div>
  );
}
